// ---------------------------------------------------------------------------
// EvaluationTemplateForm — type-only declarations.
//
// Pulled out of EvaluationTemplateForm.tsx so the form file stays focused on
// rendering + state wiring. Two type families live here:
//
//   • Form-state shapes (VariableRow, AgentDraft, TaskDraft, GraderRowState,
//     EvaluationMode, DataSourceMode, etc.) — the local UI model the form
//     drives its inputs from.
//   • Raw/server shapes (RawTask, RawNamedSet, EvaluationTemplateWithConfig,
//     etc.) — the persisted contract the seed function maps in from.
//
// Pattern constants (VARIABLE_NAME_PATTERN, TASK_NAME_PATTERN) and the
// Data Lake source-type discriminator live here too because they're
// referenced by both utils and the form.
// ---------------------------------------------------------------------------

import type { AgentSpec, VariableDeclaration } from '../../../types/agent.js';
import type { EvaluationTemplate } from '../../../types/evaluationTemplate.js';

// ── Constants ──────────────────────────────────────────────────────────────

export const VARIABLE_NAME_PATTERN = /^[A-Z][A-Z0-9_]*$/;
export const TASK_NAME_PATTERN = /^[a-z][a-z0-9-]*$/;

// Source-type discriminator for `EvaluationTemplateDataSetSpec.source` —
// matches the server's polymorphic `EvaluationTemplateDataLakeDataSource`
// JSON shape (see PatchEvaluationTemplateRequest.cs +
// EvaluationTemplateDataSetSpec.cs). Stored as a constant so the create
// payload, the seed function, and the picker all use the same string.
export const DATA_LAKE_SOURCE_TYPE = 'workbench.sources.datalake';

// ── Form-state types ───────────────────────────────────────────────────────

// How a selected file's bytes are exposed on its `content` field. Omitted
// on the wire lets the eval backend infer the mode from the file extension
// (json/jsonl -> json, text-like -> text, everything else -> base64).
export type DataLakeFileSelectorAs = 'text' | 'json' | 'base64';

// One entry in a Data Lake source's `fileSelector`. Picks the first file in
// each multi-file data item matching `firstMatching` (a glob on the file
// name) and projects it under `name` on the data envelope — so a prompt
// reads it as `$.dataItem.data.<name>.content`.
export interface DataLakeFileSelector {
  name: string;
  firstMatching: string;
  as?: DataLakeFileSelectorAs;
}

// Default binding alias for the auto-generated selector. Kept as a constant
// so the picker, the payload builder, and prompt guidance all agree on the
// `$.dataItem.data.transcript.content` path.
export const DEFAULT_FILE_SELECTOR_NAME = 'transcript';

export interface DataLakeSourceRef {
  sourceType: typeof DATA_LAKE_SOURCE_TYPE;
  dataSetId?: string;
  version?: number;
  // Required by the backend for Data Lake sources. Tells the eval harness
  // which file(s) to read from each data item and how to expose them.
  fileSelector?: DataLakeFileSelector[];
}

export type DataSourceMode = 'existing' | 'inline' | 'file';
export type AgentTypeChoice = 'llm' | 'http-proxy' | 'custom';
export type ParallelismChoice = 'task' | 'interleaved' | 'full';
export type VariableType = 'string' | 'number' | 'integer' | 'boolean';
export type EvaluationMode = 'single-run' | 'compare-agents' | 'compare-tasks';

export interface VariableRow {
  id: string;
  name: string;
  type: VariableType;
  defaultValue: string;
  description: string;
}

export interface GraderSummary {
  name: string;
  displayName?: string;
  spec?: unknown;
  variables?: Record<string, VariableDeclaration>;
  isDraft?: boolean;
  version?: number;
}

export interface GraderRowState {
  id: string;
  name: string;
  version?: number;
}

/** Lightweight catalog entry for the existing-agent picker. */
export interface AgentSummary {
  name: string;
  displayName?: string;
  spec?: AgentSpec;
  variables?: Record<string, VariableDeclaration>;
  isDraft?: boolean;
  version?: number;
}

/**
 * An agent reference. The form only supports referencing an agent that
 * already exists in the workspace store — it records the name; inline
 * authoring is not supported.
 */
export interface AgentDraft {
  id: string;
  name: string;
  version?: number;
}

// A task references one agent (`task.agent` on the wire) plus its graders.
// The form only supports single-run evaluations (one task, one agent).
export interface SingleAgentTask {
  kind: 'single';
  id: string;
  name: string;
  agent: AgentDraft;
  graderRows: GraderRowState[];
}

export type TaskDraft = SingleAgentTask;

// ── Option arrays ──────────────────────────────────────────────────────────

export const PARALLELISM_OPTIONS: {
  value: ParallelismChoice;
  label: string;
}[] = [
  {
    value: 'task',
    label: 'Task — one worker runs a full task before releasing',
  },
  { value: 'interleaved', label: 'Interleaved — workers spread across tasks' },
  { value: 'full', label: 'Full — maximum parallelism, no task ordering' },
];

export const VARIABLE_TYPE_OPTIONS: { value: VariableType; label: string }[] = [
  { value: 'string', label: 'string' },
  { value: 'number', label: 'number' },
  { value: 'integer', label: 'integer' },
  { value: 'boolean', label: 'boolean' },
];

export const EVALUATION_MODE_OPTIONS: {
  value: EvaluationMode;
  title: string;
  description: string;
}[] = [
  {
    value: 'single-run',
    title: 'Single run',
    description: 'One agent, one task — baseline evaluation',
  },
  {
    value: 'compare-agents',
    title: 'Compare agents',
    description:
      'Same queries run against multiple agents — paired statistical comparison and pick a winner',
  },
  {
    value: 'compare-tasks',
    title: 'Compare tasks',
    description:
      'Independent tasks with different agents and graders — side-by-side analysis and pick a winner',
  },
];

// ── Raw / server shapes ────────────────────────────────────────────────────
// Server-persisted fields that aren't on the canonical EvaluationTemplate
// shape yet. Optional — a missing field just renders an empty section.

export interface RawTask {
  name?: string;
  agent?: { name?: string; version?: number };
  graders?: { name?: string; version?: number }[];
  querySet?: string;
  dataSet?: string;
}

export interface RawNamedSet {
  name?: string;
  items?: string[];
  source?: DataLakeSourceRef;
}

export interface RawVariableDecl {
  type?: string;
  default?: unknown;
  description?: string;
}

export interface RawConcurrency {
  parallelism?: string;
  // Server may send `null` when the field was never set; seed handles both.
  workers?: number | null;
}

export interface EvaluationTemplateWithConfig extends EvaluationTemplate {
  tasks?: RawTask[];
  querySets?: RawNamedSet[];
  dataSets?: RawNamedSet[];
  variables?: Record<string, RawVariableDecl>;
  concurrency?: RawConcurrency;
}

export interface SeededFormState {
  jobName: string;
  jobDisplayName: string;
  jobDescription: string;
  taskDrafts: TaskDraft[];
  variables: VariableRow[];
  /** Saved variable→`$expr` bindings, recovered from the tasks' `with:` clauses. */
  bindings: Record<string, string>;
  workersText: string;
  parallelism: ParallelismChoice;
  querySetMode: DataSourceMode;
  queryInlineText: string;
  dataSetEnabled: boolean;
  dataSetMode: DataSourceMode;
  dataInlineText: string;
  // When the saved template's data set is backed by a Data Lake reference
  // (not inline items), these seed the existing-mode picker so view/edit
  // start with the right selection rather than an empty inline tab.
  dataSetExistingId: string | null;
  dataSetExistingVersion: number | null;
  // Data Lake `fileSelector` entries recovered from a saved template. Empty
  // for inline data sets or Data Lake sources authored before file
  // selectors existed. Preserved verbatim so editing never clobbers a
  // selector authored via API/CLI.
  dataSetFileSelector: DataLakeFileSelector[];
}
